import bindAll from 'lodash.bindall';
import React from 'react';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import {HashPrefix} from './hash-prefix';
import {
    defaultProjectId,
    getIsFetchingWithoutId,
    setProjectId
} from '../reducers/project-state';
import {loadShareProject,loadProjectInfo} from './service/project-info';
import {updateSaveStatus} from "../reducers/project-save";
import {setAutoSaveEnabled} from "../reducers/timeout";
import {getUser} from "./http/auth";

/* Higher Order Component to get the project id from location.hash
 * @param {React.Component} WrappedComponent: component to render
 * @returns {React.Component} component with hash parsing behavior
 */
const HashParserHOC = function (WrappedComponent) {
    class HashParserComponent extends React.Component {
        constructor (props) {
            super(props);
            bindAll(this, [
                'handleHashChange'
            ]);
        }
        componentDidMount () {
            window.addEventListener('hashchange', this.handleHashChange);
            this.handleHashChange();
        }
        componentDidUpdate (prevProps) {
            // if we are newly fetching a non-hash project...
            if (this.props.isFetchingWithoutId && !prevProps.isFetchingWithoutId) {
                // ...clear the hash from the url
                history.pushState('new-project', 'new-project',
                    window.location.pathname + window.location.search);
            }
        }
        componentWillUnmount () {
            window.removeEventListener('hashchange', this.handleHashChange);
        }
        handleHashChange () {
            this.hashMapping(window.location.hash);
        }

        hashMapping (hash) {
            hash = hash.replace(/^#/, '');
            if (hash.indexOf(HashPrefix.PROJECT) === 0) {
                const projectId = hash.replace(HashPrefix.PROJECT, '');
                loadProjectInfo(projectId.toString()).then(result=>{
                    if (result!=null){
                        this.props.setProjectId(projectId.toString());
                        const user = getUser();
                        if (result.member.id!==user.id){
                            this.props.updateSavePermission({
                                canSave:false,
                                canCreateCopy:false,
                                canRemix:false,
                                canShare: false
                            })
                        }
                    }
                })
            } else if (hash.indexOf(HashPrefix.SHARE) === 0) {
                const shareId = hash.replace(HashPrefix.SHARE, '');
                loadShareProject(shareId).then(result=>{
                    if (result!=null){
                        this.props.setProjectId(result.projectId);
                        this.props.updateSavePermission({
                            canSave:result.canEdit,
                            canCreateCopy:result.canCopy,
                            canRemix:result.canRemix,
                            canShare: false
                        })
                        this.props.onSetAutoSaveEnabled(result.canEdit)
                        return null;
                    }
                    this.props.setProjectId(defaultProjectId);
                })
                    .catch(() => {
                        this.props.setProjectId(defaultProjectId);
                    })
            } else {
                this.props.setProjectId(defaultProjectId);
            }
        }

        render () {
            const {
                /* eslint-disable no-unused-vars */
                isFetchingWithoutId: isFetchingWithoutIdProp,
                reduxProjectId,
                updateSavePermission: updateSavePermissionProp,
                onSetAutoSaveEnabled: onSetAutoSaveEnabledProp,
                setProjectId: setProjectIdProp,
                /* eslint-enable no-unused-vars */
                ...componentProps
            } = this.props;
            return (
                <WrappedComponent
                    {...componentProps}
                />
            );
        }
    }
    HashParserComponent.propTypes = {
        isFetchingWithoutId: PropTypes.bool,
        reduxProjectId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        updateSavePermission: PropTypes.func,
        onSetAutoSaveEnabled: PropTypes.func,
        setProjectId: PropTypes.func
    };
    const mapStateToProps = state => {
        const loadingState = state.scratchGui.projectState.loadingState;
        return {
            isFetchingWithoutId: getIsFetchingWithoutId(loadingState),
            reduxProjectId: state.scratchGui.projectState.projectId
        };
    };
    const mapDispatchToProps = dispatch => ({
        onSetAutoSaveEnabled: enabled => {
            dispatch(setAutoSaveEnabled(enabled));
        },
        updateSavePermission: perm =>{
            dispatch(updateSaveStatus(perm));
        },
        setProjectId: projectId => {
            dispatch(setProjectId(projectId));
        }
    });
    // Allow incoming props to override redux-provided props. Used to mock in tests.
    const mergeProps = (stateProps, dispatchProps, ownProps) => Object.assign(
        {}, stateProps, dispatchProps, ownProps
    );
    return connect(
        mapStateToProps,
        mapDispatchToProps,
        mergeProps
    )(HashParserComponent);
};

export {
    HashParserHOC as default
};
